import { Fund, Schedule } from "source/giving/donate/form/types"

const givingProfilePath = "/giving/profile"

export const ProfileNavItems = [
  { label: "My giving", path: givingProfilePath },
  {
    label: "Donation history",
    path: `${givingProfilePath}/donations`,
  },
  {
    label: "Planned giving",
    path: `${givingProfilePath}/planned`,
  },
  {
    label: "Text2Give",
    path: `${givingProfilePath}/text-to-give`,
  },
  {
    label: "Notifications",
    path: `${givingProfilePath}/notifications`,
  },
  {
    label: "Statements",
    path: `${givingProfilePath}/statements`,
  },
] as const

export type ProfileNav = (typeof ProfileNavItems)[number]

export type SendsReceiptsValue = "always" | "never"

export type SendsReceiptsOption = {
  label: string
  value: SendsReceiptsValue
}

export type GivingPerson = {
  id: string
  can_view_text2give_settings: boolean
  email_addresses: string[]
  first_name: string
  giving_preferred_email_address: string | null
  has_joint_giver: boolean
  last_name: string
  sends_receipts: SendsReceiptsValue
  text_to_give_phone_number: Text2GivePhoneNumber | null
}

export type GivingPersonResponse = {
  data: GivingPersonResource
  included: GivingPersonResourceIncludedData | []
}

export type GivingPersonResourceIncludedData = [Text2GivePhoneNumberResource]

export type Text2GivePhoneNumberResource = {
  type: "TextToGivePhoneNumber"
  id: string
  attributes: Omit<Text2GivePhoneNumber, "id" | "payment_method_id">
  relationships: {
    payment_method: { data: { type: "PaymentMethod"; id: string } | null }
  }
}

export type GivingOrganization = {
  id: string
  name: string
  text2give_enabled: boolean
  text2give_phone_number: string | null
  formatted_text2give_phone_number: string | null
  credit_cards_allowed: boolean
  church_center_settings_app_enabled: boolean
  in_kind_donations_enabled: boolean
  nondeductible_giving_enabled: boolean
  accepting_donations: boolean
}

export type Text2GivePhoneNumber = {
  id: string
  default_fund_name: string | null
  phone_number: string
  payment_method_id: string | null
  registration_completed: boolean
}

export type PaymentMethod = {
  id: string
  brand: string
  last4: string
  method_type: string
  verified: boolean
  pendingDonation?: Donation | null
}

export type Statement = {
  id: string
  amount_cents: number
  amount_currency: string
  created_at: string
  starts_on: string
  ends_on: string
  donation_count: number
  download_url: string
  name: string
}

export type StatementResource = {
  id: string
  attributes: Omit<Statement, "id">
}

export type ResourceMeta = {
  total_count: number
  next: { offset: number } | null
}

export type StatementsResource = {
  data: StatementResource[]
  meta: ResourceMeta
}

export type RecurringDonationsResponse = {
  data: RecurringDonationResource[]
  included: RecurringDonationsResourceIncludedData[]
  meta: ResourceMeta
}

export type RecurringDonationResponse = {
  data: RecurringDonationResource
  included: RecurringDonationsResourceIncludedData[]
}

export type RecurringDonationResource = {
  id: string
  type: "RecurringDonation"
  attributes: Omit<RecurringDonation, "id">
  relationships: {
    payment_method: { data: { type: "PaymentMethod"; id: string } | null }
    designations: {
      data: { type: "RecurringDonationDesignation"; id: string }[]
    }
  }
}

type RecurringDonationsResourceIncludedData =
  | RecurringDonationDesignationResource
  | FundResource

export function isDesignation(
  item: RecurringDonationsResourceIncludedData | DonationsResourceIncludedData,
): item is RecurringDonationDesignationResource | DesignationResource {
  return (
    item.type === "RecurringDonationDesignation" || item.type === "Designation"
  )
}

export function isFund(
  item:
    | RecurringDonationsResourceIncludedData
    | PledgeResourceIncludedData
    | DonationsResourceIncludedData
    | InKindDonationsResourceIncludedData,
): item is FundResource {
  return item.type === "Fund"
}

export function isPaymentSource(
  item: DonationsResourceIncludedData,
): item is PaymentSourceResource {
  return item.type === "PaymentSource"
}

export type RecurringDonationDesignationResource = {
  type: "RecurringDonationDesignation"
  id: string
  attributes: { amount_cents: number }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type RecurringDonationDesignationPayload = {
  id: string
  type: "RecurringDonationDesignation"
  attributes: {
    amount: string
  }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type NewRecurringDonationDesignationPayload = {
  type: "RecurringDonationDesignation"
  attributes: {
    amount: string
  }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type FundResource = {
  type: "Fund"
  id: string
  attributes: Omit<Fund, "id">
}

export type RecurringDonation = {
  id: string
  awaiting_verification: boolean
  designations: Designation[]
  fee_covered: boolean
  humanized_schedule: string
  last_deterministically_processed_at: string | null
  next_occurrence: string | null
  payment_method_id: string | null
  release_hold_at: string | null
  on_hold_indefinitely: boolean
  schedule: Schedule
  updated_at: string
}

export type RecurringDonationStatus =
  | "active"
  | "indefinite_hold"
  | "hold_until"

export type Designation = {
  id: string | null
  fund: Fund
  amount: string
}

export type PledgesResource = {
  data: PledgeResource[]
  included: PledgeResourceIncludedData[]
  meta: PledgesResourceMeta
}

export type PledgesResourceMeta = ResourceMeta & {
  has_concluded_pledges: boolean | null
}

export type PledgeResource = {
  id: string
  type: "Pledge"
  attributes: Omit<Pledge, "id" | "joint_giver" | "fund" | "pledge_campaign">
  relationships: {
    pledge_campaign: { data: { type: "PledgeCampaign"; id: string } }
    joint_giver: { data: { type: "Person"; id: string } | null }
    fund: { data: { type: "Fund"; id: string } }
  }
}

type PledgeResourceIncludedData =
  | IncludedGivingPersonResource
  | PledgeCampaignResource
  | FundResource

export function isGivingPerson(
  item:
    | PledgeResourceIncludedData
    | DonationsResourceIncludedData
    | InKindDonationsResourceIncludedData,
): item is IncludedGivingPersonResource {
  return item.type === "Person"
}

export function isPledgeCampaign(
  item: PledgeResourceIncludedData,
): item is PledgeCampaignResource {
  return item.type === "PledgeCampaign"
}

export type IncludedGivingPersonResource = {
  type: "Person"
  id: string
  attributes: Omit<GivingPerson, "id">
}

export type GivingPersonResource = {
  id: string
  type: "Person"
  attributes: Omit<
    GivingPerson,
    "id" | "text_to_give_phone_number" | "has_joint_giver"
  >
  relationships: {
    joint_giver: { data: { type: "JointGiver"; id: string } | null }
    text_to_give_phone_number: {
      data: {
        type: "TextToGivePhoneNumber"
        id: string
      } | null
    }
  }
}

export type PledgeCampaignResource = {
  type: "PledgeCampaign"
  id: string
  attributes: Omit<PledgeCampaign, "id" | "fund">
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type Pledge = {
  id: string
  amount_cents: number
  amount_currency: string
  fund: Fund
  joint_amount_cents: number
  joint_donated_total_cents: number
  joint_giver_amount_cents: number | null
  joint_giver: GivingPerson | null
  joint_progress_percent: number
  pledge_campaign: PledgeCampaign
}

export type PledgeCampaign = {
  id: string
  description: string | null
  ends_at: string
  fund: Fund
  goal_cents: number | null
  goal_currency: string
  name: string
  received_of_goal_percent: string | null
  received_total_cents: number | null
  show_goal_in_church_center_app: boolean
  starts_at: string
}

export type DonationsSummary = {
  all_donation_years: number[]
  all_donation_funds: Fund[]
  current_year_donations_total_amount_cents: number
  current_year_in_kind_donations_count: number
  has_in_kind_donations: boolean
  previous_year_donations_total_amount_cents: number
  previous_year_in_kind_donations_count: number
}

export type Donation = {
  id: string
  amount_cents: number
  amount_currency: string
  awaiting_verification: boolean
  completed_at: string
  failed_at: string | null
  designations: Designation[]
  fee_covered: boolean
  payment_brand: string
  payment_check_dated_at: string | null
  payment_check_number: string | null
  payment_expiration: string
  payment_fingerprint: string
  payment_identifier: string
  payment_last4: string
  payment_method: PaymentMethodType
  payment_method_sub: string
  payment_status: string
  payment_source: PaymentSource
  payment_tokenization_method: string | null
  person: GivingPerson
  processed_at: string
  received_at: string
  refunded: boolean
  tax_deductible: boolean | null
}

export type PaymentMethodType = "ach" | "cash" | "check" | "card"

export type DonationsResource = {
  data: DonationResource[]
  included: DonationsResourceIncludedData[]
  meta: DonationsResourceMeta
}

export type DonationsResourceMeta = ResourceMeta & {
  received_total_amount_cents: number
  active_total_count: number
}

export type DonationResource = {
  id: string
  type: "Donation"
  attributes: Omit<Donation, "id">
  relationships: {
    payment_source: { data: { type: "PaymentSource"; id: string } | null }
    person: { data: { type: "Person"; id: string } | null }
    designations: {
      data: { type: "Designation"; id: string }[]
    }
    fund: { data: { type: "Fund"; id: string } | null }
  }
}

export type DesignationResource = {
  type: "Designation"
  id: string
  attributes: {
    amount_cents: number
  }
  relationships: {
    fund: { data: { type: "Fund"; id: string } }
  }
}

export type PaymentSourceResource = {
  type: "PaymentSource"
  id: string
  attributes: {
    name: string
    humanized_payment_source_type: string
  }
}

export type PaymentSource = {
  id: string
  name: string
  type: string
}

type DonationsResourceIncludedData =
  | PaymentSourceResource
  | IncludedGivingPersonResource
  | DesignationResource
  | FundResource

export type InKindDonation = {
  id: string
  description: string
  received_on: string
  fund: Fund
  person: GivingPerson
  fair_market_value_cents: number | null
}

export type InKindDonationsResource = {
  data: InKindDonationResource[]
  included: InKindDonationsResourceIncludedData[]
  meta: ResourceMeta
}

export type InKindDonationResource = {
  id: string
  type: "InKindDonation"
  attributes: Omit<InKindDonation, "id">
  relationships: {
    person: { data: { type: "Person"; id: string } | null }
    fund: { data: { type: "Fund"; id: string } | null }
  }
}

type InKindDonationsResourceIncludedData =
  | IncludedGivingPersonResource
  | FundResource

export type DonationType = "monetary" | "inKind"

export const DonationTypeOptions = [
  { label: "Monetary", value: "monetary" as DonationType },
  {
    label: "In-kind",
    value: "inKind" as DonationType,
  },
]
